import type { EquippedItem, ResearchSelection } from "@sins2-atlas/compute";
import { isSubjectActive } from "@sins2-atlas/compute";
import type { ExoticPrice } from "@sins2-atlas/game-data";
import { type ResearchIndex, subjectKey, type TechSet, tick } from "./tech-set";

// The Loadout (CONTEXT.md: Loadout; ADR 0012) — the Ship Items a reader has equipped on ONE
// hull, and the URL grammar that carries them. Modelled on tech-set.ts and pure in the same
// way: no dataset import, every join handed in, so the rules are testable over plain objects.
//
// Two things separate it from a Tech Set. It belongs to one hull in one place — an entity
// page or one comparison column — so it does NOT ride unit-level links: `?items=` is
// deliberately absent from lib/frame.ts's SerializedFrame, which is what makes clicking to
// another unit start that hull empty. And an item's LEVEL is not a choice: the game levels
// items by research alone (`research_prerequisites_per_level`), so the Tech Set decides how
// strong an equipped item is and itemLevelUnder is the whole of that rule.

/** The equipped Ship Item ids. A set, never a count — the game caps every item at one
 * per hull (CONTEXT.md: Loadout). */
export type Loadout = ReadonlySet<string>;

export const EMPTY_LOADOUT: Loadout = new Set();

export interface ParsedLoadout {
  items: Loadout;
  /** Tokens naming no item this dataset ships, deduplicated in URL order — reported back
   * to the reader verbatim, and equipping nothing. */
  unknown: string[];
}

/** One `?items=` slot: a comma-separated list of item ids, empty for an empty Loadout. */
export function parseLoadoutSlot(
  value: string | undefined,
  known: (id: string) => boolean,
): ParsedLoadout {
  if (value === undefined || value === "") return { items: EMPTY_LOADOUT, unknown: [] };
  const items = new Set<string>();
  const unknown: string[] = [];
  for (const token of new Set(value.split(","))) {
    if (token === "") continue;
    if (known(token)) items.add(token);
    else unknown.push(token);
  }
  return { items, unknown };
}

/** The Loadout as its one slot value, or undefined when nothing is equipped so a bare link
 * stays canonical. */
export function serializeLoadout(loadout: Loadout | undefined): string | undefined {
  if (loadout === undefined || loadout.size === 0) return undefined;
  return [...loadout].join(",");
}

/** What an item level needs before it unlocks — the dataset's `items[].levels[]` shape,
 * narrowed to the one field this rule reads. */
export interface ItemLevelGates {
  readonly prerequisites?: readonly string[];
}

/** The level an item is at under a research selection (ADR 0012: an item's level is not a
 * choice). Levels unlock in order, so the ladder stops at the first level whose gate the
 * selection does not hold — holding level 3's subject without level 2's leaves the item at
 * level 1, which is what the game does. */
export function itemLevelUnder(
  levels: readonly ItemLevelGates[],
  research: ResearchSelection,
): number {
  let level = 1;
  for (let index = 1; index < levels.length; index++) {
    const gates = levels[index]?.prerequisites ?? [];
    if (!gates.every((subjectId) => isSubjectActive(subjectId, research))) break;
    level = index + 1;
  }
  return level;
}

/** What an equipped item is, as the dataset states it — the dataset's `items[]` narrowed to
 * the three fields the fold reads: its levels' gates, and the price the Loadout adds to the
 * hull's (Issue 47, ADR 0012). */
export interface EquippableItem {
  readonly levels: readonly ItemLevelGates[];
  readonly price: { readonly credits: number; readonly metal: number; readonly crystal: number };
  readonly exoticPrice: readonly ExoticPrice[];
  /** The ability the item gives the hull, where it gives one (`items[].ability`). An
   * item-granted ability is on no hull's own list, so the Loadout entry is the only thing
   * that can tell a fold the reader has it — which is how the Anathema's Cloak Generator
   * crest switches its Cloaking row on (Issue 58). */
  readonly ability?: { readonly id: string };
}

/** The Loadout as the fold's selector: equipped item id → the level in force and what it
 * cost. Intersected with `itemIds`, the hull's OWN shop listing, because a Loadout belongs
 * to one hull (ADR 0012) while one frame folds every unit a page shows, some of them with
 * no shop at all. Undefined when this hull carries nothing, so the fold's fast path is
 * untouched. */
export function equippedItems(
  loadout: Loadout | undefined,
  itemIds: readonly string[],
  itemOf: (id: string) => EquippableItem | undefined,
  research: ResearchSelection,
): ReadonlyMap<string, EquippedItem> | undefined {
  if (loadout === undefined || loadout.size === 0) return undefined;
  const equipped = new Map<string, EquippedItem>();
  for (const id of itemIds) {
    if (!loadout.has(id)) continue;
    const item = itemOf(id);
    if (item === undefined) continue;
    equipped.set(id, {
      level: itemLevelUnder(item.levels, research),
      price: item.price,
      exoticPrice: item.exoticPrice,
      ...(item.ability === undefined ? {} : { grantedAbilityId: item.ability.id }),
    });
  }
  return equipped.size === 0 ? undefined : equipped;
}

// ---------------------------------------------------------------------------------------
// The rules (Issue 45). A Loadout is only ever one the game could produce, and the four
// facts that decide it — the hull's Item Slots, an item's ladder rung, the items it may not
// sit beside, and the research that buys it — are handed in as a LoadoutIndex, exactly as
// tech-set.ts takes a ResearchIndex. No dataset import, so every rule below is tested
// against a hand-sized index rather than a fixture.

/** What the rules need to know about one Ship Item — the dataset's `items[]` narrowed to
 * the four fields the rules read (`neededItems`, `mutuallyExclusiveItems`,
 * `buildPrerequisites`). */
export interface IndexedItem {
  id: string;
  /** The rung below it: items that must be equipped beside it (the game's `needed_items`,
   * which means the requirement is PRESENT, not merely bought once — issue 42, in game
   * 2026-09-06). 30 of the 192 buyable items carry one, up to 3 rungs deep. */
  needs: readonly string[];
  /** Items it cannot be equipped beside (the game's `mutually_exclusive_items`): 18 buyable
   * items in 6 groups, e.g. Missile Armor against Beam Armor. */
  excludes: readonly string[];
  /** The Research Subjects gating its purchase (`buildPrerequisites`, flat AND). Same id
   * space as the Tech Set's subject keys (ADR 0009), which is what lets the two couple. */
  research: readonly string[];
}

export interface LoadoutIndex {
  items: ReadonlyMap<string, IndexedItem>;
  /** Per item id, the ids that name it in `needs` — what an un-equip takes with it. */
  dependents: ReadonlyMap<string, readonly string[]>;
  /** Per item id, everything it may not sit beside. Symmetric by construction: exclusion is
   * a relation between two items, and reading it one-way would let the order of two clicks
   * decide whether the game's rule applies. */
  exclusions: ReadonlyMap<string, ReadonlySet<string>>;
}

export function buildLoadoutIndex(items: Iterable<IndexedItem>): LoadoutIndex {
  const byId = new Map<string, IndexedItem>();
  const dependents = new Map<string, string[]>();
  const exclusions = new Map<string, Set<string>>();
  const exclude = (a: string, b: string): void => {
    const set = exclusions.get(a) ?? new Set<string>();
    set.add(b);
    exclusions.set(a, set);
  };
  for (const item of items) {
    byId.set(item.id, item);
    for (const needed of item.needs) {
      const list = dependents.get(needed) ?? [];
      list.push(item.id);
      dependents.set(needed, list);
    }
    for (const other of item.excludes) {
      exclude(item.id, other);
      exclude(other, item.id);
    }
  }
  return { items: byId, dependents, exclusions };
}

/** The Item Slots a Loadout consumes. A ladder **stacks** — one slot per rung, never one
 * slot for the ladder: an Argonev Starbase carrying Auxiliary Hangar I, II and III fills 3
 * of its 8 slots and gains all three bonuses (observed in game 2026-09-06). So the whole
 * arithmetic is the membership count, and it lives HERE alone: a future reading that some
 * ladder shares a slot changes this one function and nothing else. */
export function slotsUsed(loadout: Loadout): number {
  return loadout.size;
}

/** One rung of a hull's Item Slot capacity — the dataset's `units[].itemSlots[]`. */
export interface ItemSlotGates {
  readonly count: number;
  readonly prerequisites?: readonly string[];
}

/** How many Item Slots a hull offers under a research selection. The count is a RESEARCH
 * fact, not a level one — which is why this takes a Tech Set and no level at all: exactly
 * one hull of 39 (the Anarkrul Wanderer, 6 → 8) moves, and it moves on one subject. Rungs
 * are alternatives rather than a ladder, so the answer is the largest count whose gate the
 * selection holds; a hull with no shop has no slots. */
export function itemSlotsUnder(
  slots: readonly ItemSlotGates[],
  research: ResearchSelection,
): number {
  let count = 0;
  for (const rung of slots) {
    const gates = rung.prerequisites ?? [];
    if (!gates.every((subjectId) => isSubjectActive(subjectId, research))) continue;
    count = Math.max(count, rung.count);
  }
  return count;
}

/** Why an item came off. Never silent: a Loadout that quietly loses an item the reader
 * chose is a Loadout they cannot trust. */
type RemovalReason =
  /** Something equipped in its place that the game forbids beside it. */
  | "excluded"
  /** It is a rung above an item that just came off. */
  | "requires-removed-item"
  /** The Tech Set no longer holds the research that buys it. */
  | "research-unticked";

export interface LoadoutRemoval {
  id: string;
  reason: RemovalReason;
  /** What caused it: the item equipped that excludes it, the item removed that it needed,
   * or the Research Subject the Tech Set no longer holds. */
  because: string;
}

export interface LoadoutChange {
  loadout: Loadout;
  /** Ids equipped BESIDES the one asked for — the rungs below it. */
  added: readonly string[];
  /** Ids taken off, in the order the change took them, each with its reason. */
  removed: readonly LoadoutRemoval[];
  /** Present when the change did not happen: the hull has no room for the whole ladder
   * ("no-room"), or the ladder names two items the game forbids together ("forbidden").
   * The Loadout is then exactly what it was. */
  refused?: "no-room" | "forbidden";
}

const unchanged = (loadout: Loadout, refused?: LoadoutChange["refused"]): LoadoutChange => ({
  loadout,
  added: [],
  removed: [],
  ...(refused === undefined ? {} : { refused }),
});

/** An item and everything it needs, transitively — the ladder a single tick brings. Ids the
 * index does not know are dropped, exactly as an unknown key leaves a Tech Set alone. */
function ladderOf(id: string, index: LoadoutIndex, into: Set<string>): void {
  if (into.has(id) || !index.items.has(id)) return;
  into.add(id);
  for (const needed of index.items.get(id)?.needs ?? []) ladderOf(needed, index, into);
}

/** Take the seeded items off, then everything that needed them, breadth first so the report
 * reads in the order the removals happened. */
function removeCascade(
  carried: Set<string>,
  seeds: readonly LoadoutRemoval[],
  index: LoadoutIndex,
): LoadoutRemoval[] {
  const removed: LoadoutRemoval[] = [];
  const queue = [...seeds];
  for (let at = 0; at < queue.length; at++) {
    const removal = queue[at] as LoadoutRemoval;
    if (!carried.delete(removal.id)) continue;
    removed.push(removal);
    for (const dependent of index.dependents.get(removal.id) ?? []) {
      if (carried.has(dependent)) {
        queue.push({ id: dependent, reason: "requires-removed-item", because: removal.id });
      }
    }
  }
  return removed;
}

/** Equip one item on a hull with `capacity` Item Slots. The ladder below it comes too; every
 * item the game forbids beside anything in that ladder comes OFF, taking its own dependents
 * with it, and each removal is reported. The cap is read after the removals — a swap into a
 * full hull is legal in game — and a ladder that will not fit changes nothing at all rather
 * than equipping half of it. */
export function equip(
  loadout: Loadout,
  id: string,
  index: LoadoutIndex,
  capacity: number,
): LoadoutChange {
  if (!index.items.has(id)) return unchanged(loadout);
  const wanted = new Set<string>();
  ladderOf(id, index, wanted);
  // A ladder whose own rungs exclude each other is not a Loadout the game can produce.
  for (const member of wanted) {
    for (const forbidden of index.exclusions.get(member) ?? []) {
      if (wanted.has(forbidden)) return unchanged(loadout, "forbidden");
    }
  }
  const carried = new Set(loadout);
  const seeds: LoadoutRemoval[] = [];
  for (const member of [...wanted].sort()) {
    for (const forbidden of [...(index.exclusions.get(member) ?? [])].sort()) {
      if (carried.has(forbidden) && !wanted.has(forbidden)) {
        seeds.push({ id: forbidden, reason: "excluded", because: member });
      }
    }
  }
  const removed = removeCascade(carried, seeds, index);
  const added = [...wanted].filter((member) => member !== id && !carried.has(member));
  for (const member of wanted) carried.add(member);
  if (carried.size > capacity) return unchanged(loadout, "no-room");
  return { loadout: carried, added, removed };
}

/** Un-equip one item: it goes, and so does every rung that stood on it, reported. The rungs
 * BELOW it stay — each is an item in its own right, bought and slotted separately. */
export function unequip(loadout: Loadout, id: string, index: LoadoutIndex): LoadoutChange {
  if (!loadout.has(id)) return unchanged(loadout);
  const carried = new Set(loadout);
  carried.delete(id);
  const seeds: LoadoutRemoval[] = (index.dependents.get(id) ?? [])
    .filter((dependent) => carried.has(dependent))
    .map((dependent) => ({
      id: dependent,
      reason: "requires-removed-item" as const,
      because: id,
    }));
  const removed = removeCascade(carried, seeds, index);
  return { loadout: carried, added: [], removed };
}

/** The Research Subjects equipping these items requires — each item's own gates and its
 * whole ladder's, deduplicated and canonically ordered. The caller ticks them through the
 * Tech Set's closure, the ids being the same id space (ADR 0009): equipping an item the
 * game gates behind research puts that research in the Tech Set. */
export function researchFor(ids: Iterable<string>, index: LoadoutIndex): string[] {
  const ladder = new Set<string>();
  for (const id of ids) ladderOf(id, index, ladder);
  const subjects = new Set<string>();
  for (const member of ladder) {
    for (const subjectId of index.items.get(member)?.research ?? []) subjects.add(subjectId);
  }
  return [...subjects].sort();
}

/** The Loadout under a research selection: every equipped item the Tech Set no longer buys
 * comes off, and so does everything that stood on it. This is the other direction of the
 * same coupling — unticking research must not leave a hull carrying an item it cannot own —
 * and every removal is reported rather than silently applied. */
export function withResearch(
  loadout: Loadout,
  index: LoadoutIndex,
  research: ResearchSelection,
): LoadoutChange {
  const carried = new Set(loadout);
  const seeds: LoadoutRemoval[] = [];
  for (const id of [...loadout].sort()) {
    const missing = (index.items.get(id)?.research ?? []).find(
      (subjectId) => !isSubjectActive(subjectId, research),
    );
    if (missing !== undefined) {
      seeds.push({ id, reason: "research-unticked", because: missing });
    }
  }
  const removed = removeCascade(carried, seeds, index);
  return { loadout: carried, added: [], removed };
}

/** What a whole `?items=` slot equips: each named id in turn, so the rules that govern a
 * click govern a link too. A link naming two items the game forbids together keeps the last
 * of them, a link past the hull's slots stops at the cap, and both losses are reported. */
export function loadoutClosure(
  ids: Iterable<string>,
  index: LoadoutIndex,
  capacity: number,
): LoadoutChange {
  let loadout: Loadout = EMPTY_LOADOUT;
  const named = new Set<string>();
  const removed: LoadoutRemoval[] = [];
  let refused: LoadoutChange["refused"];
  for (const id of ids) {
    named.add(id);
    const change = equip(loadout, id, index, capacity);
    loadout = change.loadout;
    removed.push(...change.removed);
    if (refused === undefined && change.refused !== undefined) refused = change.refused;
  }
  return {
    loadout,
    added: [...loadout].filter((id) => !named.has(id)),
    removed: removed.filter((removal) => !loadout.has(removal.id)),
    ...(refused === undefined ? {} : { refused }),
  };
}

/** Whether a Loadout is one the game could produce: every item is one this index ships,
 * every rung stands on the rung below it, no two members are forbidden together, and the
 * whole thing fits the hull's Item Slots. Loadouts built by equip / unequip / loadoutClosure
 * are reachable by construction; this is the check that proves it. */
export function isReachableLoadout(
  loadout: Loadout,
  index: LoadoutIndex,
  capacity: number,
): boolean {
  if (slotsUsed(loadout) > capacity) return false;
  for (const id of loadout) {
    const item = index.items.get(id);
    if (item === undefined) return false;
    for (const needed of item.needs) if (!loadout.has(needed)) return false;
    for (const forbidden of index.exclusions.get(id) ?? []) {
      if (loadout.has(forbidden)) return false;
    }
  }
  return true;
}

/** The Tech Set equipping these items implies: every subject they are gated behind, ticked
 * through the research closure so its own prerequisite chain and its Tiers arrive with it.
 * This is the forward half of the coupling — equipping an item the game gates behind
 * research puts that research in the Tech Set — and `withResearch` is the same coupling read
 * backwards. Both halves live here because both are Loadout rules; the research closure
 * itself is never restated. */
export function techSetWithItemResearch(
  set: TechSet,
  ids: Iterable<string>,
  index: LoadoutIndex,
  research: ResearchIndex,
): TechSet {
  let next = set;
  for (const subjectId of researchFor(ids, index)) {
    next = tick(next, subjectKey(subjectId), research);
  }
  return next;
}
